import React, { useCallback, useEffect, useState } from 'react';
import store from './journalStore';
import { pad, ymd, ord, decorate } from './datesMath';

/* 💖 Days — the dates we count down to (and up from).
   Anniversaries repeat yearly, monthiversaries monthly, one-offs happen once.
   The soonest upcoming day gets a big live countdown; a one-off that has
   already happened flips into a "been N days" count-up instead. */

const EMOJIS = ['💖', '💍', '🎂', '🥂', '✈️', '🎉', '🌙', '⭐'];
const REPEATS = [
  { key: 'yearly', label: 'every year ♾️' },
  { key: 'monthly', label: 'every month 🌙' },
  { key: 'once', label: 'just once 📌' },
];

const prettyDate = (d) =>
  d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const subtitle = (ev) => {
  if (ev.repeat === 'yearly') return ev.nth > 0 ? `${ord(ev.nth)} anniversary` : 'the very first one 🥹';
  if (ev.repeat === 'monthly') return ev.nth > 0 ? `${ord(ev.nth)} monthiversary` : 'the very first one 🥹';
  if (ev.kind === 'past') return 'a day that already happened';
  return ev.kind === 'today' ? 'the big day' : 'a one-off, no pressure';
};

const pill = (ev) => {
  if (ev.kind === 'today') return 'today!! 🎉';
  if (ev.kind === 'past') return `been ${ev.days} day${ev.days === 1 ? '' : 's'} 💞`;
  if (ev.days === 1) return 'tomorrow 🫣';
  return `in ${ev.days} days`;
};

export default function Dates({ passcode }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());

  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(() => ymd(new Date()));
  const [emoji, setEmoji] = useState('💖');
  const [repeat, setRepeat] = useState('yearly');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvents(await store.events(passcode));
    } catch (e) {
      setErr('Could not load our days.');
    } finally {
      setLoading(false);
    }
  }, [passcode]);

  useEffect(() => {
    load();
  }, [load]);

  // one heartbeat per second drives the ticker AND keeps "today" honest past midnight
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const addEvent = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await store.addEvent(passcode, { title: title.trim(), date, emoji, repeat });
      setTitle('');
      setEmoji('💖');
      setRepeat('yearly');
      setDate(ymd(new Date()));
      setFormOpen(false);
      await load();
    } catch (e2) {
      setErr(e2.message || 'Could not save that day.');
    } finally {
      setSaving(false);
    }
  };

  const delEvent = async (id) => {
    try {
      await store.delEvent(passcode, id);
      await load();
    } catch (e) {
      /* ignore */
    }
  };

  const list = events.map((ev) => decorate(ev, now)).sort((a, b) => {
    if ((a.kind === 'past') !== (b.kind === 'past')) return a.kind === 'past' ? 1 : -1;
    return a.days - b.days || a.title.localeCompare(b.title);
  });
  const hero = list[0];

  // live ticker — counts down to the hero's day (or up from it, if it's a
  // one-off that already happened)
  let timer = null;
  if (hero && hero.kind !== 'today') {
    const ms = Math.abs(hero.when - now);
    const s = Math.floor(ms / 1000);
    timer = {
      d: Math.floor(s / 86400),
      h: Math.floor((s % 86400) / 3600),
      m: Math.floor((s % 3600) / 60),
      s: s % 60,
    };
  }

  return (
    <div className="aj-days">
      <div className="aj-days-head">
        <h2 className="aj-days-title">our big days</h2>
        <p className="aj-days-sub">every day with you counts. these just count louder. 😏</p>
      </div>

      {loading ? (
        <div className="aj-muted" style={{ textAlign: 'center' }}>Loading our days…</div>
      ) : (
        <>
          {hero && (
            <div className={`aj-ev-hero${hero.kind === 'today' ? ' is-today' : ''}`}>
              <button
                type="button" className="aj-del aj-ev-hero-del"
                onClick={() => delEvent(hero.id)} aria-label={`Delete ${hero.title}`}
              >
                ×
              </button>
              {hero.kind === 'today' ? (
                <>
                  <div className="aj-ev-eyebrow">drop everything</div>
                  <div className="aj-ev-hero-title aj-ev-beat">
                    🎉 {hero.emoji} {hero.title} 🎉
                  </div>
                  <div className="aj-ev-hero-sub">{subtitle(hero)} — it&rsquo;s TODAY, baby!</div>
                  <div className="aj-ev-hero-note">go celebrate us. that&rsquo;s an order 💋</div>
                </>
              ) : (
                <>
                  <div className="aj-ev-eyebrow">{hero.kind === 'past' ? 'and counting' : 'up next'}</div>
                  <div className="aj-ev-hero-title">
                    {hero.emoji} {hero.title}
                  </div>
                  <div className="aj-ev-hero-sub">
                    {subtitle(hero)} — {hero.kind === 'past' ? 'since ' : ''}
                    {prettyDate(hero.when)}
                  </div>
                  {timer && (
                    <div className="aj-ev-timer" aria-label={hero.kind === 'past' ? 'time since' : 'time until'}>
                      {[
                        [timer.d, 'days'],
                        [timer.h, 'hrs'],
                        [timer.m, 'min'],
                        [timer.s, 'sec'],
                      ].map(([n, lab]) => (
                        <div className="aj-ev-cell" key={lab}>
                          <div className="aj-ev-num">{lab === 'days' ? n : pad(n)}</div>
                          <div className="aj-ev-lab">{lab}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="aj-ev-hero-note">
                    {hero.kind === 'past'
                      ? '…and counting, forever 💞'
                      : 'patience, baby… good things take time 😏'}
                  </div>
                </>
              )}
            </div>
          )}

          {list.length > 1 && (
            <div className="aj-ev-list">
              {list.slice(1).map((ev) => (
                <div className={`aj-ev-row${ev.kind === 'today' ? ' is-today' : ''}`} key={ev.id}>
                  <span className="aj-ev-emoji" aria-hidden="true">{ev.emoji}</span>
                  <div className="aj-ev-info">
                    <div className="aj-ev-name">{ev.title}</div>
                    <div className="aj-ev-when">
                      {subtitle(ev)} · {ev.kind === 'past' ? 'since ' : ''}
                      {prettyDate(ev.when)}
                    </div>
                  </div>
                  <span className={`aj-ev-pill${ev.kind === 'today' ? ' is-today' : ev.kind === 'past' ? ' is-past' : ''}`}>
                    {pill(ev)}
                  </span>
                  <button
                    type="button" className="aj-del"
                    onClick={() => delEvent(ev.id)} aria-label={`Delete ${ev.title}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {list.length === 0 && !formOpen && (
            <div className="aj-muted" style={{ textAlign: 'center' }}>
              No big days yet — and I refuse to believe that. Add one below. 💕
            </div>
          )}

          {formOpen || list.length === 0 ? (
            <form className="aj-compose aj-ev-form" onSubmit={addEvent}>
              <input
                className="aj-input"
                value={title}
                maxLength={80}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
                placeholder="what are we counting down to, hm? 💭"
              />
              <div className="aj-ev-formrow">
                <div className="aj-moods">
                  {EMOJIS.map((em) => (
                    <button
                      type="button" key={em}
                      className={`aj-mood${emoji === em ? ' is-on' : ''}`}
                      onClick={() => setEmoji(em)}
                      aria-label={`emoji ${em}`}
                    >
                      {em}
                    </button>
                  ))}
                </div>
                <label className="aj-date-pick">
                  <span>on</span>
                  <input
                    type="date"
                    className="aj-date-input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </label>
              </div>
              <div className="aj-ev-formrow">
                <div className="aj-who">
                  {REPEATS.map((r) => (
                    <button
                      type="button" key={r.key}
                      className={`aj-chip${repeat === r.key ? ' is-on' : ''}`}
                      onClick={() => setRepeat(r.key)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <div className="aj-ev-formbtns">
                  {list.length > 0 && (
                    <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setFormOpen(false)}>
                      cancel
                    </button>
                  )}
                  <button type="submit" className="aj-btn aj-btn-sm" disabled={saving || !title.trim() || !date}>
                    {saving ? 'Saving…' : 'Save the date 💌'}
                  </button>
                </div>
              </div>
              {err && <div className="aj-err">{err}</div>}
            </form>
          ) : (
            <div className="aj-ev-addwrap">
              <button type="button" className="aj-btn-ghost" onClick={() => setFormOpen(true)}>
                ➕ add a big day
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
